import { Alert, Box, Divider, Stack } from '@mui/material';
import { subMinutes } from 'date-fns';
import { filter, isNil, lowerCase, map, property, some, upperFirst } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useDeviceHasSensor } from '@/api/device';
import {
    SoundPressureReading,
    SoundPressureSample,
    SoundPressureSampleStatsDatum,
    useSoundPressureData,
    useSoundPressureReadings,
} from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { hydrophoneIcon } from '@/assets';
import { Spectrogram, drawSpectrogram } from '@/charts/spectrogramChart';
import { SensorIcon } from '@/components/data/SensorIcon';
import { FormSelect } from '@/components/form/FormSelect';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import {
    CHART_DETAIL_PANEL_WIDTH,
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import {
    HydrophoneAudioPlayer,
    HydrophoneAudioPlayerHandle,
} from '@/components/spotter/historicalDataTab/HydrophoneAudioPlayer';
import { DataDetailSectionTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';
import {
    SpectrogramBandReadingsDetail,
    SpectrogramDataSummary,
    SpectrogramDatumDetail,
    SpectrogramReadingsSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/SpectrogramDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useChartUiContext } from '@/context/ChartUiContext';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useChartContainer } from '@/hooks/useChartContainer';
import { useChartInteractionTracking } from '@/hooks/useChartInteractionTracking';
import { useElementSize } from '@/hooks/useElementSize';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { DateRange } from '@/store';
import { isFiniteNumber } from '@/utils';

export const SpectrogramSection = () => {
    const chartTitle = 'Sound pressure spectrogram';
    const { spotterId } = useSpotterContext();
    const { hasSensor } = useDeviceHasSensor({ spotterId, type: 'hydrophone' });
    const { dateRange } = useDateRangeContext();

    const {
        data: allSensorData,
        isPending,
        isFetching,
    } = useSoundPressureData({
        spotterId: spotterId!,
        dateRange,
    });

    // only sensor positions with data
    const soundPressure = useMemo(
        () => Object.fromEntries(Object.entries(allSensorData ?? {}).filter(([_, data]) => data?.length > 0)),
        [allSensorData]
    );

    const sensorCount = Object.keys(soundPressure).length;
    const hasData = sensorCount > 0;

    if (!hasSensor && !hasData) {
        // no data in this window and no evidence that we're supposed to have the sensor; disappear
        return false;
    }

    if (!soundPressure || !hasData) {
        return (
            <CollapsibleSection
                isPending={isPending}
                isFetching={isFetching}
                title={chartTitle}
                icon={<SensorIcon src={hydrophoneIcon} />}
            >
                <Alert severity="info">No spectrogram data for selected date range</Alert>
            </CollapsibleSection>
        );
    }

    return (
        <>
            {Object.entries(soundPressure).map(([sensorPostion, dataset]) => (
                <SampleSpectrogramChart
                    key={sensorPostion}
                    dataset={dataset}
                    sensorPosition={sensorCount > 1 ? sensorPostion : null}
                />
            ))}
        </>
    );
};

/**
 * Module scope keeps this out of the render closure. A chart holds its config for its whole lifetime, so a
 * callback allocated per render would hold that render's scope alive for just as long.
 */
const bandStatsForSample = (sample: SoundPressureSample) => (sample.dataQuality !== 'present' ? [] : sample.bandStats);

const STAT_LABELS: Partial<Record<keyof SoundPressureSampleStatsDatum, string>> = {
    min: '1st percentile',
    lower: 'Lower quartile',
    median: 'Median',
    upper: 'Upper quartile',
    max: '99th percentile',
    mean: 'Mean',
};

export const SampleSpectrogramChart = ({
    dataset,
    sensorPosition,
}: {
    dataset: SoundPressureSample[];
    sensorPosition: string | null;
}) => {
    const { state: locationState } = useLocation();
    const chartTitle =
        'Sound pressure spectrogram' + (sensorPosition ? ` (${upperFirst(lowerCase(sensorPosition))})` : '');
    const { spotterId } = useSpotterContext();
    const { expandedSubsampleChartId, setExpandedSubsampleChartId } = useChartUiContext();
    const timeZone = usePreferredTimeZone();
    const { selectedTimestamp, setSelectedTimestamp, setActiveTimeline } = useTimelineNavigationContext();
    const handleUserInteraction = useChartInteractionTracking('Click spectrogram', {
        spotterId,
        sensorPosition: sensorPosition ?? '1',
    });
    const [selectedData, setSelectedData] = useState<SoundPressureSample | null>(null);
    const [anyHdrSamples, setAnyHdrSamples] = useState(false);
    const [readingsChartHasData, setReadingsChartHasData] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    /**
     * A reference, not state: the draw effect's callbacks close over the render scope, so a per-render `chart`
     * binding leaves every chart holding the one before it, and a session's windows reachable through the chain.
     */
    const chartRef = useRef<Spectrogram<SoundPressureSample, SoundPressureSampleStatsDatum> | null>(null);

    /**
     * The cursor a freshly built chart starts from. Read through a reference so that moving the cursor does
     * not tear the chart down and rebuild it.
     */
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;

    const containerId = 'spectrogram-chart' + useId();
    const { setContainer, containerRef, containerEl } = useChartContainer();
    const { width } = useElementSize(containerRef);
    const showHdrReadings = expandedSubsampleChartId === containerId;
    const drawChart = useCallback(drawSpectrogram, []);

    const hasWhiskers = useMemo(
        () => some(dataset, (d) => 'bandStats' in d && some(d.bandStats, (b) => isFiniteNumber(b.min))),
        [dataset]
    );

    const sampleStatOptions = useMemo(() => {
        const statOptions = [
            {
                id: 'mean',
                value: 'mean',
                label: 'Mean',
            },
            { decoration: <Divider sx={{ m: '2px' }} /> },
            {
                id: 'max',
                value: 'max',
                label: '99th percentile',
            },
            {
                id: 'upper',
                value: 'upper',
                label: 'Upper quartile',
            },
            {
                id: 'median',
                value: 'median',
                label: 'Median',
            },
            {
                id: 'lower',
                value: 'lower',
                label: 'Lower quartile',
            },
            {
                id: 'min',
                value: 'min',
                label: '1st percentile',
            },
        ];

        if (!hasWhiskers) {
            return filter(statOptions, (opt) => !['min', 'max'].includes(opt.value ?? ''));
        }

        return statOptions;
    }, [hasWhiskers]);

    const maxSpl = useMemo(() => {
        // pin the max color value at the max across all stats so the scale stays consistent
        let highest: number | undefined;
        for (const sample of dataset) {
            if (!('bandStats' in sample)) {
                continue;
            }
            for (const band of sample.bandStats) {
                for (const value of [band.max, band.upper]) {
                    if (isFiniteNumber(value) && (highest === undefined || value > highest)) {
                        highest = value;
                    }
                }
            }
        }
        return highest;
    }, [dataset]);

    const { control: formControl, watch: formWatch } = useForm({
        defaultValues: {
            sampleStat: 'median',
        },
        mode: 'onChange',
    });
    const [selectedSampleStat] = formWatch(['sampleStat']) as [keyof SoundPressureSampleStatsDatum];

    /**
     * Duplicate the most recent value of showExpansionBracket into a reference so we can
     * tell a freshly rendering chart what its value will be without incurring a reactive
     * re-render any time the value changes (instead we use an effect downstream of the
     * memoized showExpansionBracket to imperatively call a chart method.)
     */
    const initialShowExpansionBracket = useRef(false);

    const showExpansionBracket = useMemo(() => {
        initialShowExpansionBracket.current = readingsChartHasData && showHdrReadings;
        return initialShowExpansionBracket.current;
    }, [readingsChartHasData, showHdrReadings]);

    useEffect(() => {
        if (
            dataset?.length &&
            some(dataset, (d: SoundPressureSample) => d.dataQuality === 'present' && d.processing_source === 'hdr')
        ) {
            // setting this in an effect rather than simple memo so that if we find second-resolution data anywhere
            // we can start expecting more.
            setAnyHdrSamples(true);
        }
    }, [dataset]);

    useEffect(() => {
        if (!containerEl || !dataset?.length) {
            return;
        }

        const newChart = drawChart({
            dataset,
            config: {
                id: 'spectrogram',
                label: 'Frequency (Hz)',
                legend: { label: 'SPL (dB re 1 µPa²)' },
                valueKey: 'frequency' as const,
                colorKey: selectedSampleStat,
                maxColorValue: maxSpl,
                yAxisLabel: 'Frequency (Hz)',
                dataForBand: bandStatsForSample,
            },
            containerId,
            chartWidth: width,
            chartHeight: 438,
            showExpansionBracket: initialShowExpansionBracket.current,
            timeZone,
            onSelectTimestamp: (timestamp, timeline) => {
                // doing this inside setExpandedSubsampleChartId so that we don't have dependency on expandedSubsampleChartId which would cause a rerender after
                // every toggle
                setExpandedSubsampleChartId((oldExpandedChartId) => {
                    if (timestamp === null && oldExpandedChartId !== containerId) {
                        // timestamp === null means the currently selected timestamp was clicked again. if we're not already showing readings,
                        // turn them on instead of clearing the cursor
                        setActiveTimeline({ timestamps: timeline! });
                        return containerId;
                    } else {
                        setSelectedTimestamp(timestamp, timeline);
                        return isNil(timestamp) ? null : containerId;
                    }
                });
            },
            onUserInteraction: handleUserInteraction,
            onSelectData: (selected) => {
                if (selected) {
                    setSelectedData(selected.band);
                } else {
                    setSelectedData(null);
                }
            },
        });
        chartRef.current = newChart;
        // a chart built mid-session starts from the current cursor and bracket; the effects below own every
        // change after that
        newChart.setSelectedTimestamp(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);
        newChart.setShowExpansionBracket(initialShowExpansionBracket.current);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        containerEl,
        dataset,
        drawChart,
        containerId,
        width,
        selectedSampleStat,
        timeZone,
        maxSpl,
        setSelectedTimestamp,
        setActiveTimeline,
        handleUserInteraction,
        setExpandedSubsampleChartId,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useEffect(() => {
        chartRef.current?.setShowExpansionBracket?.(showExpansionBracket);
    }, [showExpansionBracket]);

    useEffect(() => {
        if (!selectedTimestamp && expandedSubsampleChartId === containerId) {
            setExpandedSubsampleChartId(null);
        }
    }, [selectedTimestamp, expandedSubsampleChartId, containerId, setExpandedSubsampleChartId]);

    useEffect(() => {
        if (readingsChartHasData) {
            // once we've seen it on any sample, we know they must exist. if the readings spectrogram is
            // showing data sometimes, looks better if we just show it all the time.
            setAnyHdrSamples(true);
        }
    }, [readingsChartHasData]);

    const [navigatedToTimestamp, setNavigatedToTimestamp] = useState(false);
    useEffect(() => {
        if (
            !navigatedToTimestamp &&
            locationState?.detectionClass === 'splBreach' &&
            locationState?.selectedTimestamp &&
            chartRef.current &&
            containerId &&
            sectionRef.current
        ) {
            setNavigatedToTimestamp(true);
            setSelectedTimestamp(locationState.selectedTimestamp, chartRef.current.timestamps());
            setExpandedSubsampleChartId(containerId);
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [
        navigatedToTimestamp,
        locationState,
        containerId,
        containerEl,
        setSelectedTimestamp,
        selectedTimestamp,
        setExpandedSubsampleChartId,
    ]);

    const selectedSampleDateRange = useMemo(() => {
        if (!selectedData) {
            return null;
        }

        const endDate = new Date(selectedData.timestamp);
        return {
            startDate: subMinutes(endDate, selectedData.sampleIntervalMinutes ?? 30),
            endDate,
        };
    }, [selectedData]);

    return (
        <CollapsibleSection ref={sectionRef} title={chartTitle} icon={<SensorIcon src={hydrophoneIcon} />}>
            <Stack>
                <Stack direction="row">
                    {dataset.length ? (
                        <>
                            <TrackChartViewWhenRendered
                                containerEl={containerEl}
                                chartTitle={chartTitle}
                                spotterId={spotterId!}
                            />
                            <ChartWithInfo
                                chartHeight="438px"
                                chart={<Box ref={setContainer} id={containerId} width={1} height={1} />}
                            >
                                <ChartDataSelectors>
                                    <FormSelect
                                        name="sampleStat"
                                        label=""
                                        control={formControl}
                                        options={sampleStatOptions}
                                        inputFieldProps={{
                                            id: 'stat-select',
                                            type: 'text',
                                            size: 'small',
                                        }}
                                    />
                                </ChartDataSelectors>
                                <ChartDataDetails>
                                    {!selectedData && (
                                        <SpectrogramDataSummary
                                            dataset={dataset}
                                            statKey={selectedSampleStat}
                                            statLabel={STAT_LABELS[selectedSampleStat]!}
                                        />
                                    )}
                                    {selectedData && (
                                        <>
                                            <DataDetailSectionTimestamp
                                                timestamp={selectedData.timestamp ?? null}
                                                processingSource={
                                                    'processing_source' in selectedData
                                                        ? selectedData.processing_source
                                                        : null
                                                }
                                            />
                                            <Box sx={{ m: 1 }}>
                                                <SpectrogramDatumDetail
                                                    data={selectedData}
                                                    statKey={selectedSampleStat}
                                                    statLabel={STAT_LABELS[selectedSampleStat]!}
                                                    dataset={dataset}
                                                />
                                            </Box>
                                        </>
                                    )}
                                </ChartDataDetails>
                            </ChartWithInfo>
                        </>
                    ) : (
                        <Alert severity="info">No spectrogram data for selected date range</Alert>
                    )}
                </Stack>
                {showHdrReadings && selectedData && (
                    <ReadingSpectrogramChart
                        hideLoadingIndicator={
                            /* Don't distract with a loading indicator if we're unlikely to find any readings.  */
                            selectedData.dataQuality !== 'present' || !anyHdrSamples
                        }
                        dateRange={selectedSampleDateRange!}
                        sensorPosition={selectedData.sensorPosition}
                        openedFrom="spectrogram"
                        maxSpl={maxSpl}
                        onChartRendered={setReadingsChartHasData}
                    />
                )}
            </Stack>
        </CollapsibleSection>
    );
};

const DETAILED_SPECTROGRAM_CHART_TITLES = {
    detections: 'Detailed spectrogram (detection)',
    spectrogram: 'Detailed spectrogram (sample)',
} as const;

export const ReadingSpectrogramChart = ({
    dateRange,
    sensorPosition,
    openedFrom,
    maxSpl,
    hideLoadingIndicator,
    onChartRendered,
}: {
    dateRange: DateRange;
    sensorPosition: string;
    openedFrom: 'detections' | 'spectrogram';
    maxSpl?: number;
    hideLoadingIndicator: boolean;
    onChartRendered?: (hasData: boolean, rendered?: { xTimeScale: d3.ScaleTime<number, number, never> }) => void;
}) => {
    const hydrophonePlaybackFlag = useFeatureFlag('hydrophonePlayback');
    const playerRef = useRef<HydrophoneAudioPlayerHandle>(null);
    const timeZone = usePreferredTimeZone();
    const { spotterId } = useSpotterContext();
    const { selectedSubsampleTimestamp, setSelectedSubsampleTimestamp } = useTimelineNavigationContext();
    const handleUserInteraction = useChartInteractionTracking('Select detailed spectrogram data', {
        spotterId,
        openedFrom,
    });
    const [selectedData, setSelectedData] = useState<SoundPressureReading | null>(null);
    /** @see the chart reference in {@link SampleSpectrogramChart} for why this is not state. */
    const chartRef = useRef<Spectrogram<SoundPressureReading, SoundPressureReading['bands'][number]> | null>(null);

    /**
     * The cursor a freshly built chart starts from. Read through a reference so that moving the cursor does
     * not tear the chart down and rebuild it.
     */
    const selectedSubsampleTimestampRef = useRef(selectedSubsampleTimestamp);
    selectedSubsampleTimestampRef.current = selectedSubsampleTimestamp;

    const containerId = 'spectrogram-readings-chart' + useId();
    const containerRef = useRef<HTMLElement>(null);
    const { width } = useElementSize(containerRef);
    const drawChart = useCallback(drawSpectrogram, []);

    /**
     * we are still reassigning onChartRenderedRef.current on every component render so the onChartRendered is always the latest
     * but having it inside the ref is keeping the drawChart effect from being re-run on every render, which would destroy and re-create the chart unnecessarily
     */
    const onChartRenderedRef = useRef(onChartRendered);
    onChartRenderedRef.current = onChartRendered;

    const {
        data: allSensorData,
        isPending,
        isPlaceholderData,
    } = useSoundPressureReadings({
        spotterId,
        dateRange,
        fillGaps: true,
        includeBands: true,
    });

    const dataset = useMemo(() => {
        if (!allSensorData) {
            return null;
        }
        return allSensorData[sensorPosition] ?? allSensorData['sensor-null']; // workaround for data from before readings had sensor position attached
    }, [allSensorData, sensorPosition]);

    const allTimestamps = useMemo(() => map(dataset, 'timestamp'), [dataset]);

    const hasData = useMemo(() => some(dataset, (d) => d.bands.length), [dataset]);

    /*
     * Deliberately free of fetch state: a background refetch that changes nothing must not tear the chart
     * down, so this asks only whether there is something to draw. The same value gates the container in the
     * JSX below, because the draw effect needs that container in the DOM.
     */
    const canDrawChart = hasData && !!dataset?.length;

    useEffect(() => {
        if (!canDrawChart || !dataset?.length) {
            return;
        }

        const newChart = drawChart<SoundPressureReading, SoundPressureReading['bands'][number]>({
            dataset,
            config: {
                id: 'spectrogram-readings',
                label: 'Frequency (Hz)',
                legend: { label: 'SPL (dB re 1 µPa²)' },
                valueKey: 'frequency' as const,
                colorKey: 'spl',
                maxColorValue: maxSpl,
                yAxisLabel: 'Frequency (Hz)',
                dataForBand: property('bands'),
            },
            containerId,
            chartWidth: width,
            chartHeight: 434,
            chartMargin: { top: 8 },
            showSeconds: true,
            timeZone,
            onChartRendered: (rendered) => onChartRenderedRef.current?.(true, rendered),
            onSelectData: (selected) => {
                if (selected) {
                    setSelectedData(selected.band);
                } else {
                    setSelectedData(null);
                }
            },
            onSelectTimestamp: (timestamp) => {
                setSelectedSubsampleTimestamp(timestamp, allTimestamps);
                playerRef.current?.setPlaybackTimestamp(timestamp);
            },
            onUserInteraction: handleUserInteraction,
        });
        chartRef.current = newChart;
        // a chart built mid-session starts from the current cursor; the effect below owns every change after that
        newChart.setSelectedTimestamp(
            selectedSubsampleTimestampRef.current ? new Date(selectedSubsampleTimestampRef.current) : null
        );

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        canDrawChart,
        dataset,
        drawChart,
        containerId,
        width,
        timeZone,
        setSelectedSubsampleTimestamp,
        handleUserInteraction,
        allTimestamps,
        maxSpl,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp(
            selectedSubsampleTimestamp ? new Date(selectedSubsampleTimestamp) : null
        );
    }, [selectedSubsampleTimestamp]);

    /*
     * The parent uses this state to control the expansion bracket and sample chart legend. Keep it aligned with
     * whether the chart can be drawn; using refetch status would hide the bracket and reset the legend gradient
     * whenever the data is invalidated.
     */
    useEffect(() => {
        if (!canDrawChart) {
            onChartRenderedRef.current?.(false);
        }
    }, [canDrawChart]);

    if (!hasData && hideLoadingIndicator) {
        return false;
    }

    if (canDrawChart) {
        return (
            <>
                <TrackChartViewWhenRendered
                    containerEl={containerRef}
                    chartTitle={DETAILED_SPECTROGRAM_CHART_TITLES[openedFrom]}
                    spotterId={spotterId}
                    dateRange={dateRange}
                />
                <ChartWithInfo
                    chartHeight="434px"
                    detailPanelSx={{ height: '354px' }}
                    chart={<Box ref={containerRef} id={containerId} width={1} height={1} />}
                >
                    <ChartDataDetails>
                        {!selectedData && <SpectrogramReadingsSummary dataset={dataset} />}
                        {selectedData && (
                            <>
                                <DataDetailSectionTimestamp
                                    timestamp={selectedData.timestamp ?? null}
                                    processingSource={selectedData.bands.length ? 'hdr' : undefined}
                                    includeSeconds
                                />
                                <Box sx={{ m: 1 }}>
                                    <SpectrogramBandReadingsDetail data={selectedData} />
                                </Box>
                            </>
                        )}
                    </ChartDataDetails>
                    <ChartDataSelectors>
                        {dataset && hydrophonePlaybackFlag && (
                            <HydrophoneAudioPlayer ref={playerRef} stream={dataset} />
                        )}
                    </ChartDataSelectors>
                </ChartWithInfo>
            </>
        );
    }

    /*
     * Only these two states mean the readings we hold are not the ones asked for. A refetch of the window
     * already on screen is neither, and deliberately shows nothing: keeping the chart still is worth more
     * here than announcing a refresh that usually changes nothing outside the window.
     */
    if (isPending || isPlaceholderData) {
        return (
            <LoadingIndicatorIcon
                isLoading
                loadingMessage="Loading second-resolution sound pressure readings"
                loadingSx={{
                    my: 2,
                    mx: 'auto',
                    pr: CHART_DETAIL_PANEL_WIDTH,
                }}
            />
        );
    }

    return (
        <Alert
            severity="info"
            sx={{
                my: 2,
                mr: CHART_DETAIL_PANEL_WIDTH,
                justifyContent: 'center',
            }}
        >
            Second-resolution SPL readings not available for selected sample time
        </Alert>
    );
};
